import { Card, CardContent, CardHeader, Grid, List, ListItemButton, ListItemText, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../components";
import { StatusChip } from "../components/StatusChip";
import help from "./HELP.md?raw";
import { useDashboard, type Widget } from "./useData";

const SIZES = { sm: { xs: 12, sm: 6, md: 3 }, md: { xs: 12, md: 6 }, lg: { xs: 12 } } as const;

function WidgetBody({ w }: { w: Widget }) {
  const d = w.data;
  switch (d.type) {
    case "stat":
      return (
        <>
          <Typography variant="h3" component={d.href ? RouterLink : "div"} {...(d.href ? { to: String(d.href), style: { textDecoration: "none", color: "inherit" } } : {})}>
            {String(d.value ?? "—")}
          </Typography>
          {d.label ? <Typography variant="body2" color="text.secondary">{String(d.label)}</Typography> : null}
        </>
      );
    case "list": {
      const items = (d.items ?? []) as { label: string; secondary?: string; href?: string; status?: string }[];
      return items.length === 0 ? <Typography color="text.secondary">Nothing to show</Typography> : (
        <List dense disablePadding>
          {items.map((it, i) => (
            <ListItemButton key={i} component={it.href ? RouterLink : "div"} {...(it.href ? { to: it.href } : {})} sx={{ px: 0 }}>
              <ListItemText primary={it.label} secondary={it.secondary} />
              {it.status && <StatusChip status={it.status} />}
            </ListItemButton>
          ))}
        </List>
      );
    }
    case "checklist": {
      const items = (d.items ?? []) as { label: string; done: boolean; href?: string }[];
      return (
        <List dense disablePadding>
          {items.map((it, i) => (
            <ListItemButton key={i} component={it.href ? RouterLink : "div"} {...(it.href ? { to: it.href } : {})} sx={{ px: 0, textDecoration: it.done ? "line-through" : "none" }}>
              <ListItemText primary={`${it.done ? "✓" : "○"} ${it.label}`} />
            </ListItemButton>
          ))}
        </List>
      );
    }
    case "stats": {
      const items = (d.items ?? []) as { label: string; value: string | number; status?: string }[];
      return (
        <Grid container spacing={1} component={d.href ? RouterLink : "div"} {...(d.href ? { to: String(d.href), style: { textDecoration: "none", color: "inherit" } } : {})}>
          {items.map((it, i) => (
            <Grid key={i} size={6}>
              <Typography variant="h5" color={it.status === "error" ? "error" : "inherit"}>{String(it.value ?? "—")}</Typography>
              <Typography variant="caption" color="text.secondary">{it.label}</Typography>
            </Grid>
          ))}
        </Grid>
      );
    }
    case "error":
      return <Typography color="error" variant="body2">{String(d.message)}</Typography>;
    default:
      return <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(d, null, 2)}</pre>;
  }
}

export function Home() {
  const { me } = useAuth();
  const dashboard = useDashboard();
  return (
    <>
      <PageHeader help={help} title={`Welcome, ${me?.first_name || me?.email}`} subtitle={me?.active_organization?.name} />
      {dashboard.isLoading && <LoadingState />}
      {dashboard.isError && <ErrorState message={dashboard.error.message} onRetry={() => void dashboard.refetch()} />}
      {dashboard.data && dashboard.data.length === 0 && (
        <EmptyState title="Your dashboard is empty" description="Widgets appear here as modules are enabled for your organization." />
      )}
      <Grid container spacing={2}>
        {dashboard.data?.map((w) => (
          <Grid key={w.key} size={SIZES[w.size] ?? SIZES.sm}>
            <Card sx={{ height: "100%" }}>
              <CardHeader title={w.title} slotProps={{ title: { variant: "subtitle2" } }} sx={{ pb: 0 }} />
              <CardContent><WidgetBody w={w} /></CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );
}
